import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Intercom from 'react-intercom';
import _isEmpty from 'lodash/isEmpty';
import _find from 'lodash/find';
import _isArray from 'lodash/isArray';
import _isBoolean from 'lodash/isBoolean';

import Loader from '../Loader/Loader';
import Toast from '../Toast/Toast';
import OfflineModal from '../OfflineModal/OfflineModal';
import AudioVideoUploadingModal from '../AudioVideoUploadingModal/AudioVideoUploadingModal';
import ProctorContainer from '../../containers/ProctorContainer';
import SocketContainer from '../../containers/SocketContainer';
import ReportIssueContainer from '../../containers/ReportIssueContainer';
import OfflineBanner from '../../containers/OfflineBannerContainer';
import { setLibraryLoaded, getTechnologies } from '../../actions/coding';
import { clearLocalData } from '../../actions/test';
import { codeEditorLibraryUrl } from '../../constants/AppConstants';
import PreparingTestModal from '../Instructions/PreparingTestModal';
import useCustomRedirect from '../../hooks/useCustomRedirect';
import { codingProblemTypes } from '../../constants/ProblemTypes';
import { EAudioRecordingStatus } from '../ProblemTypes/Audio/constants';
import { errorLogger, proctoringUBALogger } from '../../utils/trackingUtil';

export default function RootComponents() {
  const isProgressEnabled = useSelector(state => state.progress.enable);
  const {
    intercomUserHash,
    intercomRecruiterAppId,
    intercomHackerAppId,
  } = useSelector(state => state.appStatus.intercomIds);
  const { sectionsNotGrouped } = useSelector(state => state.sections);
  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);
  const test = useSelector(state => state.test);
  const socket = useSelector(state => state.socket);
  const multiSessionActive = useSelector(state => state.proctor.multiSession);
  const [userDetails, setUserDetails] = useState(null);
  const [appId, setAppId] = useState(null);
  const { user, initUserDetails } = auth;
  const { company, is_expired, sections } = test;
  const modalData = useMemo(() => {
    const isVideoUploading = Object.values(test.isUploadInProgress).filter(
      inProgress => inProgress === true
    );
    const isAudioUploading = Object.values(test.isAudioUploadInProgress).filter(
      inProgress => inProgress === EAudioRecordingStatus.UPLOADING
    );
    return {
      show:
        (isVideoUploading.length > 0 || isAudioUploading.length > 0) &&
        test.isTestSubmitting,
      config: {
        audioUploadInProgress: !!isAudioUploading.length,
        videoUploadInProgress: !!isVideoUploading.length,
      },
    };
  }, [
    test.isTestSubmitting,
    test.isUploadInProgress,
    test.isAudioUploadInProgress,
  ]);

  useCustomRedirect();

  // Setting up intercom
  useEffect(() => {
    if (user && company && intercomUserHash) {
      let details;
      let dateOfJoining = user.date_joined;
      // need to generate epoch for date
      if (dateOfJoining) dateOfJoining = new Date(dateOfJoining).valueOf();
      if (initUserDetails.usertype === 'recruiter') {
        setAppId(intercomRecruiterAppId);
        let dateOfCreation = auth.created;
        if (dateOfCreation) dateOfCreation = new Date(dateOfCreation).valueOf();
        details = {
          name: user.full_name,
          username: user.username,
          email: user.email,
          created_at: dateOfJoining,
          user_hash: intercomUserHash,
          type: 'recruiter',
          phone: auth.phone,
          job_title: auth.job_title,
          role: auth.role,
          company: {
            id: auth.id,
            name: company.name,
            slug: company.slug,
            created_at: dateOfCreation,
            profile_url: `https://doselect.com/team/${company.slug}`,
            team_size: auth.team_size ? auth.team_size : undefined,
          },
        };
      } else if (initUserDetails.usertype === 'hacker') {
        setAppId(intercomHackerAppId);
        details = {
          name: user.full_name,
          username: user.username,
          email: user.email,
          created_at: dateOfJoining,
          user_hash: intercomUserHash,
          phone: auth.phone,
          profile_url: `https://doselect.com/@${user.username}`,
        };
      }
      if (details) setUserDetails(details);
    }
  }, [
    user,
    initUserDetails,
    company,
    auth,
    intercomUserHash,
    intercomRecruiterAppId,
    intercomHackerAppId,
  ]);

  /**
   * If there are coding problems in the test then download the
   * editor library
   */
  useEffect(() => {
    if (
      !window.firebolt &&
      (!_isEmpty(sections) || !_isEmpty(sectionsNotGrouped))
    ) {
      const sectionList = !_isEmpty(sections) ? sections : sectionsNotGrouped;
      let hasCodingProblem = false;
      _find(sectionList, section => {
        let temp;
        if (_isArray(section.problems))
          temp = section.problems.find(problem =>
            codingProblemTypes.includes(problem.problem_type)
          );
        if (temp) {
          hasCodingProblem = true;
          return true;
        }
        return false;
      });
      if (!hasCodingProblem) {
        proctoringUBALogger(
          'Firebolt:Editor',
          'Skip loading firebolt library as there are no coding problems in the test'
        );
        return;
      }

      proctoringUBALogger(
        'Firebolt:Editor',
        'Loading firebolt library as there are coding problems in the test'
      );

      // Prevent duplicate script load
      if (document.getElementById('firebolt-editor-script')) {
        proctoringUBALogger(
          'Firebolt:Editor',
          'Skip loading firebolt library as script tag already present'
        );
        return;
      }

      dispatch(getTechnologies());

      const script = document.createElement('script');
      script.id = 'firebolt-editor-script';
      script.type = 'text/javascript';
      script.src = codeEditorLibraryUrl;
      script.async = true;

      const onLoad = () => {
        if (window.firebolt) {
          proctoringUBALogger(
            'Firebolt:Editor',
            'Firebolt library loaded successfully'
          );
          dispatch(setLibraryLoaded());
        } else {
          proctoringUBALogger(
            'Firebolt:Editor',
            'Firebolt loaded but not available on window'
          );
        }
        script.removeEventListener('load', onLoad);
      };

      const onError = error => {
        errorLogger('Failed to load code editor library', {
          source: 'RootComponents.firebolt.script',
          src: codeEditorLibraryUrl,
          error,
        });
        script.removeEventListener('error', onError);
      };

      script.addEventListener('load', onLoad);
      script.addEventListener('error', onError);

      const headScript = document.getElementsByTagName('script')[0];
      headScript.parentNode.insertBefore(script, headScript);
    }
  }, [sections, sectionsNotGrouped]);

  useEffect(() => {
    if (_isBoolean(is_expired) && is_expired) {
      dispatch(clearLocalData());
    }
  }, [is_expired]); // eslint-disable-line

  // used to check if webcam is accessable
  if (!navigator.getMedia) {
    // use the proper vendor prefix
    navigator.getMedia =
      navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia;
  }

  if (multiSessionActive) {
    return <></>;
  }

  // TODO: Render all the component while it's needed, and show loader until all mandatory api's call completed to render this component
  return (
    <>
      {!!isProgressEnabled && <Loader />}
      <AudioVideoUploadingModal
        show={modalData.show}
        modalConfig={modalData.config}
      />
      <Toast />
      <OfflineBanner />
      <OfflineModal />
      {test.slug && !socket.isSessionActive && <ProctorContainer />}
      <SocketContainer />
      <ReportIssueContainer />
      <PreparingTestModal />
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      {appId && userDetails && <Intercom appID={appId} {...userDetails} />}
    </>
  );
}
